import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useAtom } from 'jotai';
import { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import {
  filterValidIpAddresses,
  validateIpAddresses
} from '@/features/TicketCreateDialog';
import useStyles from '@/features/TicketCreateDialog/TicketCreateDialog.styles';
import { isLayoutMobileAtom } from '@/store/layout';

const ServiceFormChangeRequestInternalNetworks = () => {
  const [isLayoutMobile] = useAtom(isLayoutMobileAtom);
  const {
    control,
    formState: { errors }
  } = useFormContext<{
    custom_data: {
      environments: {
        add: string;
        remove: string;
      };
    };
  }>();
  const { classes } = useStyles({ errors });

  const [environmentsAdd, environmentsRemove] = useWatch({
    control,
    name: ['custom_data.environments.add', 'custom_data.environments.remove']
  });
  const rows = 5;

  const errorTextAdd = useMemo(() => {
    if (errors.custom_data?.environments?.add) {
      const { ipAddressesArrayInvalid } =
        filterValidIpAddresses(environmentsAdd);
      return `The following IP Addresses contain errors: ${ipAddressesArrayInvalid}`;
    }
    return null;
  }, [errors.custom_data]);

  const errorTextRemove = useMemo(() => {
    if (errors.custom_data?.environments?.remove) {
      const { ipAddressesArrayInvalid } =
        filterValidIpAddresses(environmentsRemove);
      return `The following IP Addresses contain errors: ${ipAddressesArrayInvalid}`;
    }
    return null;
  }, [errors.custom_data]);

  return (
    <>
      <Typography component="h2" variant="h6">
        Internal Networks
      </Typography>
      <Typography variant="body2" sx={{ mt: '0!important' }}>
        Please provide the public scope IP address(es) considered internal to
        the enterprise network that should be added or removed from your
        Internal Networks. RFC 1918 and other private scope IP addresses are
        automatically included.
      </Typography>
      <Stack direction={isLayoutMobile ? 'column' : 'row'} sx={{ gap: 2 }}>
        <div className={classes.ticketCreateDialogFormElement}>
          <InputLabel htmlFor="environments-textarea-add">
            Add New IP Addresses
          </InputLabel>
          <FormControl fullWidth>
            <Controller
              control={control}
              name="custom_data.environments.add"
              rules={{
                required: false,
                validate: (value) => {
                  return validateIpAddresses(value);
                }
              }}
              render={({ field }) => (
                <TextField
                  error={
                    errors.custom_data?.environments &&
                    !!errors.custom_data.environments.add
                  }
                  helperText={
                    errors.custom_data?.environments?.add ? errorTextAdd : null
                  }
                  slotProps={{
                    htmlInput: {
                      className: classes.ticketCreateDialogMultilineInput
                    }
                  }}
                  multiline
                  placeholder="Enter one entry per line"
                  rows={rows}
                  {...field}
                />
              )}
            />
          </FormControl>
        </div>
        <div className={classes.ticketCreateDialogFormElement}>
          <InputLabel htmlFor="environments-textarea-remove">
            Remove Current IP Addresses{' '}
            <small>
              (
              <Link
                color="primary"
                href="/administration/environments"
                rel="noreferrer"
                target="_blank"
                underline="none"
              >
                current networks
              </Link>
              )
            </small>
          </InputLabel>
          <Controller
            control={control}
            name="custom_data.environments.remove"
            rules={{
              required: false,
              validate: (value) => {
                return validateIpAddresses(value);
              }
            }}
            render={({ field }) => (
              <FormControl fullWidth>
                <TextField
                  error={
                    errors.custom_data?.environments &&
                    !!errors.custom_data.environments.remove
                  }
                  helperText={
                    errors.custom_data?.environments?.remove
                      ? errorTextRemove
                      : null
                  }
                  slotProps={{
                    htmlInput: {
                      className: classes.ticketCreateDialogMultilineInput
                    }
                  }}
                  multiline
                  placeholder="Enter one entry per line"
                  rows={rows}
                  {...field}
                />
              </FormControl>
            )}
          />
        </div>
      </Stack>
    </>
  );
};

export default ServiceFormChangeRequestInternalNetworks;
